/**
 * Composable report (finance, income, user statistics).
 *
 * staleTime: 1 menit untuk report (data analitik, tidak perlu real-time).
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery, useMutation } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchReportFinance,
  downloadReportFinance,
  fetchOmzet,
  fetchOmzetReal,
  fetchStatisticOmzet,
  fetchOrderCount,
  fetchOrderStatistics,
  fetchWeight,
  fetchOrderAgenList,
  fetchOrderTransactions,
  fetchOrderAgenTransactions,
  fetchOmzetDomestik,
  fetchOrderDomestik,
  fetchDomestikStatistics,
  fetchWeightDomestik,
  fetchOrderTransactionsDomestik,
  fetchReportAgen,
  fetchReportAgenStatistics,
  fetchReportMember,
  fetchReportMemberStatistics,
  fetchReportUser,
} from "@/queries/api/report";

const REPORT_STALE = 60 * 1000; // 1 menit

function p(input) {
  return unref(input);
}

// function reportQuery(keyArr, fn, options = {}) {
//   return useQuery({
//     queryKey: keyArr,
//     queryFn: fn,
//     staleTime: REPORT_STALE,
//     ...options,
//   });
// }

/* ============================================================
 * Finance
 * ============================================================ */

export function useReportFinance(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.report.finance(p(params))),
    queryFn: () => fetchReportFinance(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useDownloadReportFinance(options = {}) {
  return useMutation({
    mutationFn: ({ params, config } = {}) =>
      downloadReportFinance(params, config),
    ...options,
  });
}

/* ============================================================
 * Income (International)
 * ============================================================ */

export function useOmzet(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "omzet", p(params)]),
    queryFn: () => fetchOmzet(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOmzetReal(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "omzet-real", p(params)]),
    queryFn: () => fetchOmzetReal(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useStatisticOmzet(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "statistic-omzet", p(params)]),
    queryFn: () => fetchStatisticOmzet(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderCount(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-count", p(params)]),
    queryFn: () => fetchOrderCount(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderStatistics(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-statistics", p(params)]),
    queryFn: () => fetchOrderStatistics(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useWeight(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "weight", p(params)]),
    queryFn: () => fetchWeight(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderAgenList(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-agen-list", p(params)]),
    queryFn: () => fetchOrderAgenList(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderTransactions(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-transactions", p(params)]),
    queryFn: () => fetchOrderTransactions(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderAgenTransactions(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-agen-transactions", p(params)]),
    queryFn: () => fetchOrderAgenTransactions(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

/* ============================================================
 * Income (Domestik)
 * ============================================================ */

export function useOmzetDomestik(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.report.incomeDomestik(p(params))),
    queryFn: () => fetchOmzetDomestik(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderDomestik(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-domestik", p(params)]),
    queryFn: () => fetchOrderDomestik(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useDomestikStatistics(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "domestik-statistics", p(params)]),
    queryFn: () => fetchDomestikStatistics(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useWeightDomestik(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "weight-domestik", p(params)]),
    queryFn: () => fetchWeightDomestik(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useOrderTransactionsDomestik(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.all, "order-transactions-domestik", p(params)]),
    queryFn: () => fetchOrderTransactionsDomestik(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

/* ============================================================
 * Report User
 * ============================================================ */

export function useReportAgen(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.user(p(params)), "agen"]),
    queryFn: () => fetchReportAgen(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useReportAgenStatistics(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.user(p(params)), "agen-stat"]),
    queryFn: () => fetchReportAgenStatistics(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useReportMember(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.user(p(params)), "member"]),
    queryFn: () => fetchReportMember(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useReportMemberStatistics(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.user(p(params)), "member-stat"]),
    queryFn: () => fetchReportMemberStatistics(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}

export function useReportUser(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.report.user(p(params)), "user"]),
    queryFn: () => fetchReportUser(p(params)),
    staleTime: REPORT_STALE,
    ...options,
  });
}
